// PARTNER-PORTÁL · EGY sor a jegy-szálon — a megjelenítés a KÖZÖS `ThreadMessage`-ben, az AFFORDANCIÁK itt.
//
// MIÉRT KÜLÖN KOMPONENS (és nem a route-fájlban): a sor most már állapotot tart (szerkesztő-piszkozat) és két
// mutációt visz (szerkesztés · reakció-toggle). A route ettől 620 sor felett járt volna, és a szál-logika ott
// összekeveredett volna a lap-szerkezettel.
//
// AMI ITT SZÁNDÉKOSAN NINCS a belső szálhoz képest: FAJTA-VÁLASZTÓ — a portálon nincs belső jegyzet, minden
// komment `external` (a szerver kényszeríti).
//
// A SZERKESZTŐ RICH-TEXT (user-kérés): a komment tárolt alakja HTML, tehát sima szövegmezővel egy formázott
// vagy médiát tartalmazó komment szerkesztése elvesztette volna a formázást és a médiát. A kép-/videó-utak a
// PARTNER végpontjaira injektálva mennek — ugyanaz, amit a komment-író sáv is használ.
import type { PortalTicketDetail } from '@atlas/contracts';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DomainBadge } from '@/components/shared/domain-badge';
import { EditedMarker } from '@/components/shared/edited-marker';
import { ThreadMessage } from '@/components/shared/thread-message';
import { toggleReactionList } from '@/components/tickets/reaction-toggle';
// A `REACTION_CHOICES` itt CSAK TÍPUSKÉNT kell (a szerződés-enum szűkítése a body-hoz) — a felajánlott
// készletet maga a `TicketCommentReactions` rendereli.
import {
  TicketCommentReactions,
  type REACTION_CHOICES,
} from '@/components/tickets/ticket-comment-reactions';
import { Button } from '@/components/ui/button';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { portalKeys } from '@/features/portal/api';
import {
  PORTAL_NEW_TICKET_IMAGE_RESOLVER,
  uploadPortalEditorImage,
} from '@/features/portal/editor-images';
import { api } from '@/lib/api';
import type { EditorImageResolverConfig } from '@/lib/editor-image-src';
import { formatDateTime } from '@/lib/format-datetime';
import { isRichTextBlank, looksLikeStoredHtml } from '@/lib/sanitize-html';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type PortalComment = PortalTicketDetail['comments'][number];

/**
 * OPTIMISTA cache-frissítés a portál RÉSZLET-válaszában (a belső oldal lista-válaszban dolgozik). A SZABÁLY
 * mindkét helyen a közös `toggleReactionList` — itt csak a beágyazott alak bejárása van.
 */
export function toggleReactionInDetailCache(
  prev: unknown,
  commentId: string,
  emoji: string,
): unknown {
  const page = prev as { status?: number; body?: PortalTicketDetail } | undefined;
  if (page?.status !== 200 || !page.body || !Array.isArray(page.body.comments)) return prev;
  const comments = page.body.comments.map((c) =>
    c.id === commentId ? { ...c, reactions: toggleReactionList(c.reactions, emoji) } : c,
  );
  return { ...page, body: { ...page.body, comments } };
}

export interface PortalTicketCommentProps {
  comment: PortalComment;
  ticketId: string;
  /** A jegy FIÓKJA — a szerkesztőbe beszúrt kép/videó cél-fiókja (a tenant ebből oldódik fel a szerveren). */
  accountId: string;
  /** Reagálhat-e a hívó — a részlet `canReply`-ja (`portal.tickets.write` jog nélkül nincs írás, és a szerver is 403-at ad). */
  canReact: boolean;
  /**
   * A komment-törzsbe BEÁGYAZOTT képek feloldója (a jegyhez kötött, szűk végpont). Mindkét oldal
   * rich-textet ír, tehát bármelyik kommentben lehet kép/videó — a tárolt stabil utat pedig a böngésző nem
   * tudja betölteni.
   */
  imageResolver?: EditorImageResolverConfig | undefined;
}

export function PortalTicketComment({
  comment: c,
  ticketId,
  accountId,
  canReact,
  imageResolver,
}: PortalTicketCommentProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const optimistic = c.id.startsWith('optimistic-');

  // A szerver-állapot változásakor (mentés → új verzió) a piszkozat visszaáll a mentettre.
  useEffect(() => {
    setDraft(c.body);
    setEditing(false);
  }, [c.id, c.version, c.body]);

  const editMutation = useEntityMutation<{ body: string }>({
    mutationFn: (v) =>
      api.portal.ticketReplyUpdate.mutation({
        params: { id: ticketId, commentId: c.id },
        body: v,
        headers: { 'if-match': String(c.version) },
      }),
    invalidateKeys: [portalKeys.ticketDetail(ticketId)],
    messages: {
      success: t('toast:portal.replyUpdated'),
      conflict: t('toast:portal.replyConflict'),
      forbidden: t('toast:portal.forbidden'),
      validation: t('toast:portal.validation'),
      error: t('toast:portal.error'),
    },
  });

  /**
   * REAKCIÓ-TOGGLE — OPTIMISTA. A `mutationFn` a 4xx-et DOBJA: a ts-rest react-query mutációja a 4xx-et NEM
   * dobja (a promise a `{ status, body }`-vel RESOLVE-ol), tehát az `onError` sosem futna, és a hiba csendben
   * visszaállított chipként jelenne meg — pontosan ez a hiba volt mérve a belső szálon. A közös
   * `useEntityMutation`-t itt azért nem használjuk, mert annak optimista `update`-je nem kapja meg a mutáció
   * VÁLTOZÓJÁT (nekünk az emoji kell hozzá).
   */
  const reactionMutation = useMutation({
    mutationFn: async (emoji: string) => {
      const res = await api.portal.ticketReplyReactionToggle.mutation({
        params: { id: ticketId, commentId: c.id },
        query: { lang: i18n.language === 'en' ? 'en' : 'hu' },
        body: { emoji: emoji as (typeof REACTION_CHOICES)[number] },
      });
      if (res.status >= 400) throw new Error(`portal-reaction-toggle:${String(res.status)}`);
      return res;
    },
    onMutate: async (emoji: string) => {
      const key = portalKeys.ticketDetail(ticketId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshot = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (prev: unknown) =>
        toggleReactionInDetailCache(prev, c.id, emoji),
      );
      return { snapshot, key };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) queryClient.setQueryData(ctx.key, ctx.snapshot);
      toast.error(t('toast:portal.error'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.ticketDetail(ticketId) });
    },
  });

  // ÜRES-e a piszkozat: a LÁTHATÓ szöveg hosszán mérünk, nem a markupon (`<p></p>` nem tartalom).
  const canSave = !isRichTextBlank(draft) && !editMutation.isPending;
  // AZ AKCIÓ-SOR eltűnik, ha (a) a sor még OPTIMISTA (nincs mire hivatkozni a szerveren), vagy (b) épp
  // SZERKESZTÉS alatt van: a reakció-chipek a sor szegélyén ülnek, a szerkesztő pedig ugyanoda nyit
  // mentés/mégsem gombokat — a két vezérlő-csoport egymásra lapolt (user-döntés). A
  // piszkozatra reagálni amúgy sem értelmes.
  const showActions = !optimistic && !editing;

  return (
    <div className="relative">
      <ThreadMessage
        // NÉV-FALLBACK: feloldatlan szerző = TÖRÖLT (offboardolt) személy — a semleges „Törölt felhasználó"
        // megy ki. A korábbi ág az `authorIsPartner`-en dőlt el, ami csak a HÍVÓ SAJÁT kommentjén true, így
        // egy törölt PARTNER-kolléga kommentje „MORGENS ügyfélszolgálat"-ként jelent meg — ügyfél-oldali
        // tartalom MORGENS-nek attribuálva (mérve).
        authorName={c.authorName ?? t('common.deletedUser')}
        avatarUrl={c.authorAvatarUrl}
        accentColor={c.authorTeamColor}
        mine={c.authorIsPartner}
        pending={optimistic}
        badges={
          c.unread ? (
            <DomainBadge kind="marker" value="newReply" label={t('portal.tickets.newReply')} />
          ) : null
        }
        meta={
          <span className="inline-flex items-center gap-2">
            <Text variant="secondary" as="span" className="text-[12px]">
              {optimistic
                ? t('portal.tickets.sending')
                : formatDateTime(c.createdAt, i18n.language)}
            </Text>
            {!optimistic && <EditedMarker editedAt={c.editedAt} />}
          </span>
        }
      >
        {editing ? (
          <div className="space-y-2">
            {/* RICH-TEXT a szerkesztésben is (user-kérés): a komment tárolt alakja HTML, tehát a
                szerkesztőnek is azt kell adnia — sima szövegmezővel egy formázott (vagy képet tartalmazó)
                komment szerkesztése ELVESZTETTE volna a formázást és a médiát.
                Az a11y-NÉV KÜLÖNBÖZIK a composerétől: a lapon egyszerre ott a komment-írás és ez a
                szerkesztő, és két azonos nevű `textbox` a képernyőolvasóban (és a tesztben)
                megkülönböztethetetlen. */}
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              aria-label={t('portal.tickets.editReplyLabel')}
              disabled={editMutation.isPending}
              imageAccountId={accountId}
              uploadImage={(file) => uploadPortalEditorImage(file, accountId)}
              imageResolver={PORTAL_NEW_TICKET_IMAGE_RESOLVER}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={editMutation.isPending}
                onClick={() => {
                  setDraft(c.body);
                  setEditing(false);
                }}
              >
                {t('portal.tickets.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="brand"
                disabled={!canSave}
                onClick={() =>
                  editMutation.mutate(
                    // A szerkesztő HTML-t ad — szövegként NEM trimmelünk (a `<p>` burkolat tartalom).
                    { body: draft },
                    { onSuccess: () => setEditing(false) },
                  )
                }
              >
                {editMutation.isPending ? t('portal.tickets.saving') : t('portal.tickets.save')}
              </Button>
            </div>
          </div>
        ) : looksLikeStoredHtml(c.body) ? (
          // A MIGRÁLT/belső eredetű törzs HTML lehet — DOMPurify-sanitizálva (stored-XSS fal).
          <RichTextContent html={c.body} {...(imageResolver ? { imageResolver } : {})} />
        ) : (
          <p className="whitespace-pre-line">{c.body}</p>
        )}
      </ThreadMessage>

      {/* A KÁRTYA SZEGÉLYÉRE ülő AKCIÓ-SOR — UGYANAZ a minta és UGYANAZ a pozíció, mint a belső szálon
          (`-bottom-3 right-4`): a reakció-chipek, a reakció-hozzáadás és a szerkesztés egy csoportban, a
          jobb-alsó sarokban, a szegélyt „átlépve". MINDIG látszik (nem hoverre): mobilon nincs hover, és a
          rejtett akcióról a felhasználó nem tudja, hogy létezik. A szerkesztés IKON-ONLY, a felirat
          tooltipben. Szerkesztés alatt az EGÉSZ sor elrejtve (a mentés/mégsem gombok ugyanoda esnének). */}
      {showActions && (
        <div className="absolute -bottom-3 right-4 z-10 flex items-center gap-1.5">
          <TicketCommentReactions
            reactions={c.reactions}
            canReact={canReact}
            pending={reactionMutation.isPending}
            onToggle={(emoji) => reactionMutation.mutate(emoji)}
          />
          {c.canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  aria-label={t('portal.tickets.editReply')}
                  className="border-border bg-card text-muted-foreground hover:border-brand hover:text-foreground ease-atlas flex items-center rounded-full border p-1.5 shadow-sm transition-colors duration-150"
                >
                  <PencilSquareIcon className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{t('portal.tickets.editReply')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
